import { Component, ElementRef, Inject, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FilehandlerService } from '@app/core/services/file/filehandler.service';
import { SnackbarService } from '@app/core/services/snackbar/snackbar.service';
import { UserService } from '@app/core/services/user/user.service';
import { LoginStateService } from '@app/features/auth/services/login.state.service';
import { FileUploaded, FILE_REMOVED, FILE_UPDATED } from '@app/shared/models/file.interface';
import { DialogBaseComponent } from '@components/modals/dialog-base/dialog-base.component';
import { TranslateService } from '@ngx-translate/core';
import { throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { ImagesAllowed } from '../../upload/upload.component';
import { AvatarUploadModel } from '../avatar.interface';

@Component({
  selector: 'vfs-avatar-dialog',
  templateUrl: './avatar-dialog.component.html',
  styleUrls: ['./avatar-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AvatarDialogComponent extends DialogBaseComponent implements OnInit {
  @ViewChild('inputFile') inputFile: ElementRef;
  newImageUrl = '';
  newImageId = '';
  previousImageId = '';
  image: AvatarUploadModel;
  imageChanged = '';

  constructor(
    dialogRef: MatDialogRef<AvatarDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    @Inject('LoginStateService') protected loginStateService: LoginStateService,
    protected userService: UserService,
    private fileHandlerService: FilehandlerService,
    protected translate: TranslateService,
    private snackbarService: SnackbarService
  ) {
    super(dialogRef);
  }

  ngOnInit(): void {
    this.previousImageId = this.data.view.image;
  }

  selectImage() {
    this.previousImageId = this.data.view.image;
    this.inputFile.nativeElement.onchange = () => {
      let file = this.inputFile.nativeElement.files[0];
      if (this.fileHandlerService.checkIfImageAllowed(file)) {
        let reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          this.image = {
            file,
            preview: reader.result
          };
        };
        this.imageChanged = FILE_UPDATED;
      } else {
        this.snackbarService
          .openSnackBar(this.translate.instant('core.files.imagesAllowed'), 'warning', '', true, 5000)
          .subscribe(_ => {});
      }
    };
    this.inputFile.nativeElement.click();
  }

  submitPicture() {
    if (this.imageChanged === FILE_UPDATED) {
      this.uploadFile();
    } else if (this.imageChanged === FILE_REMOVED) {
      this.removeUserImage();
    }
  }

  /**
   * Function to upload file
   * @param file file to uplaod
   */
  public uploadFile() {
    const fd = new FormData();
    let imageData: FileUploaded;
    fd.append('file', this.image.file);
    fd.append('module', 'USR');
    this.image.sub = this.fileHandlerService
      .uploadFile(fd)
      .pipe(
        catchError(error => throwError(error)),
        tap(imageResponse => {
          imageData = imageResponse;
        }),
        // switchMap(imageResponse => this.userService.updateUserImage(this.data.view.id, imageResponse.id)),
        catchError(error => throwError(error))
      )
      .subscribe(_ => {
        this.updateUserImage(imageData);
        this.close(imageData);
      });
  }

  updateUserImage(evt) {
    if (evt.url) {
      this.setSessionImage();
      this.newImageUrl = evt.url;
      this.newImageId = evt.id;
      this.image.preview = null;
    }
  }

  removeUserImageTemp() {
    this.previousImageId = this.data.view.image;
    this.data.view.image = null;
    this.imageChanged = FILE_REMOVED;
  }

  removeUserImage() {
    this.fileHandlerService.deleteUserImage(this.data.view.id).subscribe(_ => {
      this.setSessionImage();
      this.close();
    });
  }

  cancelChange() {
    this.data.view.image = this.previousImageId;
    this.close();
  }

  /**
   * Put the image of the user in the session if it is the same one that has been edited
   */
  setSessionImage() {
    let session = this.loginStateService.getSession();
    if (this.data.view.username === session.user.username) {
      this.userService.getByUsername(this.data.view.username).subscribe(user => {
        session.user.image = user.image;
        this.loginStateService.setUser(user);
        this.loginStateService.setSession(session);
      });
    }
  }

  getImagesAllowed() {
    return ImagesAllowed;
  }
}
